import { useState, useRef, useEffect } from "react";
import { classNames } from "../../helpers/classNames";

export default function Pagination({
  pageCount = 1,
  onPageChange = () => {},
  currentPage = 0,
  paginationClasses,
}) {
  const isFirst = currentPage === 0;
  const isLast = currentPage === pageCount - 1;

  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState(currentPage + 1);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!inputVisible) {
      setInputValue(currentPage + 1);
    }
  }, [currentPage, inputVisible]);

  useEffect(() => {
    if (inputVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputVisible]);

  const goToPage = (page) => {
    if (page >= 0 && page < pageCount) {
      onPageChange({ selected: page });
    }
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      handlePageChange();
    }
  };

  const handlePageChange = () => {
    const pageNum = parseInt(inputValue);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= pageCount) {
      goToPage(pageNum - 1);
    }
    setInputVisible(false);
  };

  return (
    <div
      className={classNames(
        "flex justify-end items-end gap-1.5 mt-6",
        paginationClasses
      )}
    >
      <button
        className="text-blue-600 hover:bg-blue-100 text-xl transition-all disabled:opacity-30 rounded-full w-10 h-10 flex items-center justify-center border border-transparent hover:border-blue-300"
        onClick={() => goToPage(0)}
        disabled={isFirst}
      >
        «
      </button>
      <button
        className="text-blue-600 hover:bg-blue-100 text-xl transition-all disabled:opacity-30 rounded-full w-10 h-10 flex items-center justify-center border border-transparent hover:border-blue-300"
        onClick={() => goToPage(currentPage - 1)}
        disabled={isFirst}
      >
        ‹
      </button>

      <div className="flex items-center gap-1 text-sm font-medium text-gray-700">
        <span>Page</span>

        <div
          onClick={() => setInputVisible(true)}
          className={classNames(
            "relative transition-all duration-300 ease-in-out",
            inputVisible ? "w-14" : "w-12"
          )}
        >
          {inputVisible ? (
            <input
              ref={inputRef}
              type="number"
              className="w-full h-10 px-2 text-center border border-blue-300 bg-white text-blue-700 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all text-base shadow-sm"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleEnter}
              onBlur={handlePageChange}
              min={1}
              max={pageCount}
            />
          ) : (
            <div className="cursor-pointer w-full h-10 px-2 border border-gray-300 bg-gray-50 text-blue-700 rounded-md font-medium flex items-center justify-center hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm">
              {currentPage + 1}
            </div>
          )}
        </div>

        <span>of {pageCount}</span>
      </div>

      <button
        className="text-blue-600 hover:bg-blue-100 text-xl transition-all disabled:opacity-30 rounded-full w-10 h-10 flex items-center justify-center border border-transparent hover:border-blue-300"
        onClick={() => goToPage(currentPage + 1)}
        disabled={isLast}
      >
        ›
      </button>
      <button
        className="text-blue-600 hover:bg-blue-100 text-xl transition-all disabled:opacity-30 rounded-full w-10 h-10 flex items-center justify-center border border-transparent hover:border-blue-300"
        onClick={() => goToPage(pageCount - 1)}
        disabled={isLast}
      >
        »
      </button>
    </div>
  );
}
